import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import Swal from 'sweetalert2';
import { 
  XMarkIcon, 
  SparklesIcon, 
  PlusIcon, 
  TrashIcon, 
  CheckIcon, 
  TagIcon, 
  BuildingStorefrontIcon, 
  GlobeAltIcon,
  ShieldCheckIcon
} from '@heroicons/react/24/outline';
import { obtenerPoliticaDescuentos, guardarPoliticaDescuentos } from '../api/backend_v2';

const ModalPoliticaDescuentos = ({ isOpen, onClose, onSaved }) => {
  const [loading, setLoading] = useState(true);
  const [guardando, setGuardando] = useState(false);

  // Estado de la política
  const [general, setGeneral] = useState({
    receta: { efectivo: 25, transferencia: 20 },
    sol: { efectivo: 20, transferencia: 20 }
  });
  const [excepciones, setExcepciones] = useState([]);

  // Formulario para nueva excepción
  const [mostrandoFormNueva, setMostrandoFormNueva] = useState(false);
  const [nuevaRegla, setNuevaRegla] = useState({
    nombre: '',
    marcasInput: '',
    efectivo: 15,
    transferencia: 10,
    max_cuotas: 3
  });

  useEffect(() => {
    if (isOpen) {
      cargarPolitica();
    }
  }, [isOpen]);

  const cargarPolitica = async () => {
    setLoading(true);
    try {
      const data = await obtenerPoliticaDescuentos();
      if (data) {
        if (data.general) {
          setGeneral({
            receta: {
              efectivo: Number(data.general.receta?.efectivo ?? 25),
              transferencia: Number(data.general.receta?.transferencia ?? 20)
            },
            sol: {
              efectivo: Number(data.general.sol?.efectivo ?? 20),
              transferencia: Number(data.general.sol?.transferencia ?? 20)
            }
          });
        }
        if (Array.isArray(data.excepciones)) {
          setExcepciones(data.excepciones);
        }
      }
    } catch (error) {
      console.error('Error cargando politica:', error);
      Swal.fire('Error', 'No se pudo cargar la política de descuentos', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleGeneralChange = (tipo, metodo, valor) => {
    const num = Math.max(0, Math.min(100, parseFloat(valor) || 0));
    setGeneral(prev => ({
      ...prev,
      [tipo]: {
        ...prev[tipo],
        [metodo]: num
      }
    }));
  };

  const handleEliminarExcepcion = (index) => {
    setExcepciones(prev => prev.filter((_, i) => i !== index));
  };

  const handleAgregarExcepcion = (e) => {
    e.preventDefault();
    if (!nuevaRegla.nombre.trim()) {
      Swal.fire('Atención', 'Ingresá un nombre para la regla (ej: Proveedor Cacic)', 'warning');
      return;
    }
    const marcasArray = nuevaRegla.marcasInput
      .split(',')
      .map(m => m.trim().toLowerCase())
      .filter(Boolean);

    if (marcasArray.length === 0) {
      Swal.fire('Atención', 'Ingresá al menos una marca separada por coma', 'warning');
      return;
    }

    const nueva = {
      id: `rule_${Date.now()}`,
      nombre: nuevaRegla.nombre.trim(),
      marcas: marcasArray,
      efectivo: parseFloat(nuevaRegla.efectivo) || 0,
      transferencia: parseFloat(nuevaRegla.transferencia) || 0,
      max_cuotas: nuevaRegla.max_cuotas ? parseInt(nuevaRegla.max_cuotas, 10) : null
    };

    setExcepciones(prev => [...prev, nueva]);
    setNuevaRegla({
      nombre: '',
      marcasInput: '',
      efectivo: 15,
      transferencia: 10,
      max_cuotas: 3
    });
    setMostrandoFormNueva(false);
  };

  const handleGuardar = async () => {
    setGuardando(true);
    try {
      const payload = {
        general,
        excepciones
      };
      await guardarPoliticaDescuentos(payload);
      Swal.fire({
        icon: 'success',
        title: '¡Políticas actualizadas!',
        text: 'Los descuentos para mostrador y e-commerce se guardaron correctamente.',
        timer: 2000,
        showConfirmButton: false
      });
      if (typeof onSaved === 'function') {
        onSaved(payload);
      }
      onClose();
    } catch (error) {
      console.error('Error guardando politica:', error);
      Swal.fire('Error', 'No se pudieron guardar las políticas', 'error');
    } finally {
      setGuardando(false);
    }
  };

  if (!isOpen) return null;

  return (
      <div className="fixed inset-0 z-[1050] overflow-y-auto bg-slate-900/70 backdrop-blur-sm flex items-center justify-center p-4">
        <div className="bg-white rounded-3xl shadow-2xl max-w-3xl w-full overflow-hidden border border-slate-200 animate-fadeIn my-auto">

        {/* Header */}
        <div className="bg-gradient-to-r from-slate-900 to-slate-800 text-white p-6 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-cyan-500/20 border border-cyan-400/30 flex items-center justify-center text-cyan-300">
              <TagIcon className="w-6 h-6" />
            </div>
            <div>
              <h2 className="text-xl font-black text-white">Políticas Comerciales y Descuentos</h2>
              <p className="text-xs text-slate-300">Configuración multi-tenant para mostrador y tienda online</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="text-slate-400 hover:text-white transition-colors p-1.5 rounded-lg hover:bg-slate-700/50"
          >
            <XMarkIcon className="w-6 h-6" />
          </button>
        </div>

        {/* Contenido */}
        <div className="p-6 space-y-6 max-h-[75vh] overflow-y-auto">
          
          {/* Banner Informativo */}
          <div className="bg-cyan-50 border border-cyan-200 rounded-2xl p-4 text-xs text-cyan-900 flex items-start gap-3">
            <SparklesIcon className="w-5 h-5 text-cyan-600 shrink-0 mt-0.5" />
            <div className="space-y-1">
              <span className="font-bold block">Reglas de precios automáticas:</span>
              <span>
                Los precios de armazones se calculan según estas políticas. En el <strong>mostrador</strong> se utiliza el porcentaje de <strong>Efectivo</strong>, mientras que en la <strong>tienda online</strong> se aplica el porcentaje de <strong>Transferencia</strong>.
              </span>
            </div>
          </div>

          {loading ? (
            <div className="py-12 text-center text-slate-400 text-sm">
              Cargando políticas comerciales...
            </div>
          ) : (
            <>
              {/* SECCIÓN 1: DESCUENTOS BASE GENERALES */}
              <div className="space-y-3">
                <h3 className="text-sm font-black text-slate-800 uppercase tracking-wider flex items-center gap-2 border-b border-slate-200 pb-2">
                  <span className="w-5 h-5 rounded-full bg-cyan-600 text-white text-[11px] flex items-center justify-center font-bold">1</span>
                  Descuentos Base Generales (por Tipo de Producto)
                </h3>
                
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  
                  {/* Receta */}
                  <div className="bg-slate-50 border border-slate-200 rounded-2xl p-4 space-y-3">
                    <div className="flex items-center justify-between">
                      <span className="font-black text-slate-800 text-sm">👓 Armazones de Receta</span>
                      <span className="text-[10px] font-bold text-slate-500 uppercase bg-slate-200 px-2 py-0.5 rounded-md">General</span>
                    </div>
                    
                    <div className="grid grid-cols-2 gap-3 pt-1">
                      <div>
                        <label className="block text-[11px] font-bold text-slate-600 mb-1 flex items-center gap-1">
                          <BuildingStorefrontIcon className="w-3.5 h-3.5 text-emerald-600" />
                          % Efectivo (Local)
                        </label>
                        <div className="flex items-center">
                          <input
                            type="number"
                            min="0"
                            max="100"
                            value={general.receta.efectivo}
                            onChange={(e) => handleGeneralChange('receta', 'efectivo', e.target.value)}
                            className="w-full bg-white border border-slate-300 text-slate-800 font-black text-center rounded-xl py-2 px-3 text-sm focus:border-cyan-500 focus:outline-none"
                          />
                          <span className="font-bold text-slate-500 ml-1.5">%</span>
                        </div>
                      </div>

                      <div>
                        <label className="block text-[11px] font-bold text-slate-600 mb-1 flex items-center gap-1">
                          <GlobeAltIcon className="w-3.5 h-3.5 text-blue-600" />
                          % Transferencia (Web)
                        </label>
                        <div className="flex items-center">
                          <input
                            type="number"
                            min="0"
                            max="100"
                            value={general.receta.transferencia}
                            onChange={(e) => handleGeneralChange('receta', 'transferencia', e.target.value)}
                            className="w-full bg-white border border-slate-300 text-slate-800 font-black text-center rounded-xl py-2 px-3 text-sm focus:border-cyan-500 focus:outline-none"
                          />
                          <span className="font-bold text-slate-500 ml-1.5">%</span>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Sol */}
                  <div className="bg-slate-50 border border-slate-200 rounded-2xl p-4 space-y-3">
                    <div className="flex items-center justify-between">
                      <span className="font-black text-slate-800 text-sm">🕶️ Anteojos de Sol</span>
                      <span className="text-[10px] font-bold text-slate-500 uppercase bg-slate-200 px-2 py-0.5 rounded-md">General</span>
                    </div>
                    
                    <div className="grid grid-cols-2 gap-3 pt-1">
                      <div>
                        <label className="block text-[11px] font-bold text-slate-600 mb-1 flex items-center gap-1">
                          <BuildingStorefrontIcon className="w-3.5 h-3.5 text-emerald-600" />
                          % Efectivo (Local)
                        </label>
                        <div className="flex items-center">
                          <input
                            type="number"
                            min="0"
                            max="100"
                            value={general.sol.efectivo}
                            onChange={(e) => handleGeneralChange('sol', 'efectivo', e.target.value)}
                            className="w-full bg-white border border-slate-300 text-slate-800 font-black text-center rounded-xl py-2 px-3 text-sm focus:border-cyan-500 focus:outline-none"
                          />
                          <span className="font-bold text-slate-500 ml-1.5">%</span>
                        </div>
                      </div>

                      <div>
                        <label className="block text-[11px] font-bold text-slate-600 mb-1 flex items-center gap-1">
                          <GlobeAltIcon className="w-3.5 h-3.5 text-blue-600" />
                          % Transferencia (Web)
                        </label>
                        <div className="flex items-center">
                          <input
                            type="number"
                            min="0"
                            max="100"
                            value={general.sol.transferencia}
                            onChange={(e) => handleGeneralChange('sol', 'transferencia', e.target.value)}
                            className="w-full bg-white border border-slate-300 text-slate-800 font-black text-center rounded-xl py-2 px-3 text-sm focus:border-cyan-500 focus:outline-none"
                          />
                          <span className="font-bold text-slate-500 ml-1.5">%</span>
                        </div>
                      </div>
                    </div>
                  </div>

                </div>
              </div>

              {/* SECCIÓN 2: EXCEPCIONES POR MARCAS / PROVEEDORES */}
              <div className="space-y-3 pt-2">
                <div className="flex items-center justify-between border-b border-slate-200 pb-2">
                  <h3 className="text-sm font-black text-slate-800 uppercase tracking-wider flex items-center gap-2">
                    <span className="w-5 h-5 rounded-full bg-cyan-600 text-white text-[11px] flex items-center justify-center font-bold">2</span>
                    Reglas Especiales por Marcas (Excepciones Comerciales)
                  </h3>

                  {!mostrandoFormNueva && (
                    <button
                      type="button"
                      onClick={() => setMostrandoFormNueva(true)}
                      className="text-xs font-bold bg-cyan-50 hover:bg-cyan-100 text-cyan-700 border border-cyan-200 px-3 py-1.5 rounded-xl flex items-center gap-1.5 transition-colors"
                    >
                      <PlusIcon className="w-4 h-4" />
                      Agregar Regla de Marcas
                    </button>
                  )}
                </div>

                {/* Formulario Nueva Excepción */}
                {mostrandoFormNueva && (
                  <form onSubmit={handleAgregarExcepcion} className="bg-amber-50/70 border border-amber-200 rounded-2xl p-4 space-y-3 animate-fadeIn">
                    <div className="flex items-center justify-between">
                      <span className="font-bold text-xs text-amber-900 uppercase tracking-wider">Nueva Regla por Marca</span>
                      <button
                        type="button"
                        onClick={() => setMostrandoFormNueva(false)}
                        className="text-slate-400 hover:text-slate-600 text-xs font-semibold"
                      >
                        Cancelar
                      </button>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      <div>
                        <label className="block text-xs font-bold text-slate-700 mb-1">Nombre de la Regla o Proveedor *</label>
                        <input
                          type="text"
                          required
                          placeholder="Ej: Proveedor Cacic"
                          value={nuevaRegla.nombre}
                          onChange={(e) => setNuevaRegla(prev => ({ ...prev, nombre: e.target.value }))}
                          className="w-full bg-white border border-slate-300 rounded-xl p-2.5 text-xs text-slate-800 focus:outline-none focus:border-amber-500"
                        />
                      </div>

                      <div>
                        <label className="block text-xs font-bold text-slate-700 mb-1">Marcas (separadas por coma) *</label>
                        <input
                          type="text"
                          required
                          placeholder="Ej: Rusty, Vulk, Maria Cher, Jellvet"
                          value={nuevaRegla.marcasInput}
                          onChange={(e) => setNuevaRegla(prev => ({ ...prev, marcasInput: e.target.value }))}
                          className="w-full bg-white border border-slate-300 rounded-xl p-2.5 text-xs text-slate-800 focus:outline-none focus:border-amber-500"
                        />
                      </div>
                    </div>

                    <div className="grid grid-cols-3 gap-3">
                      <div>
                        <label className="block text-[11px] font-bold text-slate-700 mb-1">% Efectivo (Local)</label>
                        <input
                          type="number"
                          min="0"
                          max="100"
                          value={nuevaRegla.efectivo}
                          onChange={(e) => setNuevaRegla(prev => ({ ...prev, efectivo: e.target.value }))}
                          className="w-full bg-white border border-slate-300 rounded-xl p-2 text-xs text-center font-bold text-slate-800"
                        />
                      </div>

                      <div>
                        <label className="block text-[11px] font-bold text-slate-700 mb-1">% Transferencia (Web)</label>
                        <input
                          type="number"
                          min="0"
                          max="100"
                          value={nuevaRegla.transferencia}
                          onChange={(e) => setNuevaRegla(prev => ({ ...prev, transferencia: e.target.value }))}
                          className="w-full bg-white border border-slate-300 rounded-xl p-2 text-xs text-center font-bold text-slate-800"
                        />
                      </div>

                      <div>
                        <label className="block text-[11px] font-bold text-slate-700 mb-1">Máx Cuotas s/interés</label>
                        <input
                          type="number"
                          min="1"
                          max="24"
                          placeholder="Ej: 3"
                          value={nuevaRegla.max_cuotas}
                          onChange={(e) => setNuevaRegla(prev => ({ ...prev, max_cuotas: e.target.value }))}
                          className="w-full bg-white border border-slate-300 rounded-xl p-2 text-xs text-center font-bold text-slate-800"
                        />
                      </div>
                    </div>

                    <button
                      type="submit"
                      className="w-full bg-amber-500 hover:bg-amber-600 text-slate-950 font-bold py-2 rounded-xl text-xs transition-colors shadow-sm"
                    >
                      Guardar Regla de Marcas
                    </button>
                  </form>
                )}

                {/* Lista de Excepciones */}
                {excepciones.length === 0 ? (
                  <p className="text-xs text-slate-400 italic py-2">
                    No hay reglas especiales cargadas. Todas las marcas utilizarán los descuentos base generales.
                  </p>
                ) : (
                  <div className="space-y-3">
                    {excepciones.map((exc, idx) => (
                      <div
                        key={exc.id || idx}
                        className="bg-slate-50 border border-slate-200 hover:border-slate-300 rounded-2xl p-4 flex flex-col sm:flex-row sm:items-center justify-between gap-3 transition-colors"
                      >
                        <div className="space-y-1.5">
                          <div className="flex items-center gap-2">
                            <span className="font-black text-slate-800 text-sm">{exc.nombre}</span>
                            {exc.max_cuotas && (
                              <span className="text-[10px] font-bold bg-amber-100 text-amber-800 border border-amber-200 px-2 py-0.5 rounded-full">
                                Máx {exc.max_cuotas} cuotas
                              </span>
                            )}
                          </div>
                          
                          {/* Badges de marcas */}
                          <div className="flex flex-wrap gap-1">
                            {(exc.marcas || []).map((m, mIdx) => (
                              <span
                                key={mIdx}
                                className="text-[11px] font-bold bg-white border border-slate-300 text-slate-700 px-2 py-0.5 rounded-lg capitalize shadow-2xs"
                              >
                                {m}
                              </span>
                            ))}
                          </div>
                        </div>

                        <div className="flex items-center justify-between sm:justify-end gap-4 border-t sm:border-t-0 pt-2 sm:pt-0">
                          <div className="flex items-center gap-3 text-xs">
                            <div className="text-right">
                              <span className="text-[10px] text-slate-500 block uppercase font-bold">Efectivo</span>
                              <span className="font-black text-emerald-600">{exc.efectivo}% OFF</span>
                            </div>
                            <div className="h-6 w-px bg-slate-200" />
                            <div className="text-right">
                              <span className="text-[10px] text-slate-500 block uppercase font-bold">Transferencia</span>
                              <span className="font-black text-blue-600">{exc.transferencia}% OFF</span>
                            </div>
                          </div>

                          <button
                            type="button"
                            onClick={() => handleEliminarExcepcion(idx)}
                            className="text-slate-400 hover:text-rose-600 p-1.5 rounded-lg hover:bg-rose-50 transition-colors"
                            title="Eliminar regla"
                          >
                            <TrashIcon className="w-4 h-4" />
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </>
          )}

        </div>

        {/* Footer */}
        <div className="bg-slate-50 border-t border-slate-200 p-4 px-6 flex items-center justify-between">
          <button
            type="button"
            onClick={onClose}
            className="text-xs font-bold text-slate-600 hover:text-slate-800 px-4 py-2.5 rounded-xl hover:bg-slate-200/60 transition-colors"
          >
            Cancelar
          </button>

          <button
            type="button"
            onClick={handleGuardar}
            disabled={guardando || loading}
            className="bg-cyan-600 hover:bg-cyan-500 text-white font-extrabold text-xs px-6 py-2.5 rounded-xl shadow-lg shadow-cyan-600/20 flex items-center gap-2 transition-all disabled:opacity-50"
          >
            {guardando ? (
              <span>Guardando Políticas...</span>
            ) : (
              <>
                <CheckIcon className="w-4 h-4" />
                <span>Guardar Políticas de la Óptica</span>
              </>
            )}
          </button>
        </div>

      </div>
    </div>
  );
};

ModalPoliticaDescuentos.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onSaved: PropTypes.func
};

export default ModalPoliticaDescuentos;
